import { ChangeEvent } from "react";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { formatDateTimeBkk } from "../../utils/date";

interface ReviewCheckboxCellProps {
  checked: boolean;
  /** Shown under the box so a later reader can tell who signed — the pair may swap shifts. */
  reviewerName: string | null;
  /** ISO instant the sign-off was stamped, shown under the name. */
  reviewedAt: string | null;
  /** The box is not this reader's to tick at all — the column belongs to the other side. */
  disabled: boolean;
  /**
   * Why an otherwise reachable box is dead, shown on hover. A reason is a refusal in itself,
   * so it disables the box as well as explaining it. Null when the box is live, and null too
   * for a reader who does not own the column: there is nothing to tell someone who was never
   * being asked to sign.
   */
  disabledReason?: string | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * One ตรวจแล้ว sign-off: the box, and beneath it who signed and when.
 *
 * The name matters because the two people who sign are not always the same — finance and
 * accounting cover for each other — so "ticked" alone does not say who to ask about a figure.
 * The timestamp matters for the same reason a day later: a row queried a week on needs to
 * show whether the sign-off came before or after the correction being argued about.
 *
 * Rendered in Bangkok wall-clock and in the same `DD/MM/YYYY HH:mm` shape as the
 * วัน เวลา การโอน column, so a sign-off and the transfer it signs off read on one clock.
 */
export default function ReviewCheckboxCell({
  checked,
  reviewerName,
  reviewedAt,
  disabled,
  disabledReason = null,
  onChange,
}: ReviewCheckboxCellProps) {
  const reviewedAtLabel = formatDateTimeBkk(reviewedAt);
  const boxDisabled = disabled || Boolean(disabledReason);

  return (
    <TableCell align="center">
      {/* The span is what carries the hover: a disabled input fires no pointer events. */}
      <Tooltip title={disabledReason ?? ""}>
        <span>
          <Checkbox
            checked={checked}
            disabled={boxDisabled}
            onChange={onChange}
          />
        </span>
      </Tooltip>
      {checked && (reviewerName || reviewedAtLabel) && (
        // Tight line height: this sits in a dense table and must not grow the row twice
        // over just because the sign-off now carries two lines instead of one.
        <Stack sx={{ lineHeight: 1.3 }}>
          {reviewerName && (
            <Typography
              variant="caption"
              color="text.secondary"
              lineHeight={1.3}
              noWrap
            >
              {reviewerName}
            </Typography>
          )}
          {reviewedAtLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              lineHeight={1.3}
              noWrap
            >
              {reviewedAtLabel} น.
            </Typography>
          )}
        </Stack>
      )}
    </TableCell>
  );
}
